import React, {useEffect, useState, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux';
import { ProductPagePreviewDialog } from '../../dialog'
import PageWorkspacePanelComponent from './pageWorkspacePanel.component'
import {setPageWorkspaceLanguage} from "../../../redux/slice/productPage.slice";
import {fetchCustomTemplateHtml, fetchPreviewTemplateSettings} from "../../../redux/action/template.action";
import {unwrapResult} from "@reduxjs/toolkit";
import { fetchPreviewLanguageData } from "app/feature/constants/constants.action";

export default function PageWorkspacePanelContainer() {
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false)
  const lang = useSelector(state => state.constant.languages);
  const productLanguages = useSelector(state => state.productPage.lang);
  const currentLanguage = useSelector(state => state.productPage.pageWorkspaceLanguage);
  const profileLanguage = useSelector(state => state.profile.language);

  // Track whether the user has manually selected a language
  const [userSelectedLanguage, setUserSelectedLanguage] = useState(false);
  // Keep track of initial mount
  const initialMount = useRef(true);

  const dispatch = useDispatch();
  const templateAfter = useSelector(state => state.productPage.templateAfter);
  const templateDropdown = useSelector(state => state.productPage.templateDropdown);
  const customTemplateHtml = useSelector(state => state.products.template.workspaceSetting.customHtml);
  const [isCustomUi, setIsCustomUi] = useState(false);
  useEffect(() => {
    if (templateAfter && templateAfter !== -1) {
      const templateSelected = templateDropdown.find(template => template.uuid === templateAfter);
      if (templateSelected?.is_custom_ui) {
        dispatch(fetchCustomTemplateHtml({template: templateSelected.settings_json_url, isGenuine: true}))
            .then(unwrapResult)
            .then(() => setIsCustomUi(true));
      }
      else {
        dispatch(fetchPreviewTemplateSettings(templateSelected?.settings_json_url))
            .then(unwrapResult)
            .then(() => setIsCustomUi(false));
      }
    }
    else {
      setIsCustomUi(false)
    }
  }, [templateAfter, dispatch, templateDropdown]);

  // Map profile language to product language format
  const mapProfileLanguageToProductLanguage = (profileLang) => {
    if (!profileLang) return 'EN'; // Default to English if no profile language

    // Convert profile language to uppercase
    const upperProfileLang = profileLang.toUpperCase();

    // Handle special cases and ensure consistent format
    if (upperProfileLang === 'ZH-CN' || upperProfileLang === 'ZH_CN' || upperProfileLang === 'ZH') return 'ZH';
    if (upperProfileLang === 'MS-MY' || upperProfileLang === 'MS_MY' || upperProfileLang === 'MS') return 'MS';
    if (upperProfileLang === 'ZH-TW' || upperProfileLang === 'ZH_TW' || upperProfileLang === 'ZHTW') return 'ZH-TW';
    if (upperProfileLang === 'ID' || upperProfileLang === 'ID_ID') return 'ID';
    if (upperProfileLang === 'TH' || upperProfileLang === 'TH_TH') return 'TH';
    if (upperProfileLang === 'VI' || upperProfileLang === 'VI_VN') return 'VI';
    if (upperProfileLang === 'TL' || upperProfileLang === 'TL_PH') return 'TL';
    if (upperProfileLang === 'EN' || upperProfileLang === 'EN_US') return 'EN';

    return upperProfileLang;
  };

  // Force sync with profile language on component mount
  useEffect(() => {
    // This effect runs once on component mount to force sync with profile language
    if (profileLanguage && productLanguages) {
      // Map profile language to product language format and set it
      const mappedLanguage = mapProfileLanguageToProductLanguage(profileLanguage);
      dispatch(setPageWorkspaceLanguage(mappedLanguage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means this runs once on mount

  // Only sync with profile language on initial load and when languages are added
  // but not when user manually selects a language
  useEffect(() => {
    // Check if we have all the necessary data
    if (!profileLanguage) {
      return;
    }

    if (!productLanguages || !Array.isArray(productLanguages) || productLanguages.length === 0) {
      return;
    }

    // Only sync with profile language if this is the initial mount or if no user selection has been made
    if (initialMount.current || !userSelectedLanguage) {
      // Map profile language to product language format
      const mappedLanguage = mapProfileLanguageToProductLanguage(profileLanguage);

      // Find the exact case of the profile language in the product languages
      const exactLanguage = productLanguages.find(
          lang => lang.toUpperCase() === mappedLanguage
      ) || mappedLanguage; // Fallback to mapped language if not found

      // Only update if the language is different from the current one
      if (exactLanguage && exactLanguage !== currentLanguage) {
        dispatch(setPageWorkspaceLanguage(exactLanguage));
      }

      // Mark initial mount as complete
      if (initialMount.current) {
        initialMount.current = false;
      }
    }
  }, [profileLanguage, productLanguages, dispatch, currentLanguage, userSelectedLanguage]);

  const handlePreview = () => {
    setIsPreviewDialogOpen(true)
  }

  // Allow user to select a language through dropdown
  // This doesn't change the profile language, just the current view
  const onSetLanguage = (language) => {
    // Mark that user has manually selected a language to prevent profile language override
    setUserSelectedLanguage(true);

    // Update the language CODE in the Redux store
    dispatch(setPageWorkspaceLanguage(language));
    // Fetch and store the language DATA for the preview
    dispatch(fetchPreviewLanguageData({ language }));
  }

  return (
      <>
        <PageWorkspacePanelComponent
            handlePreview={handlePreview}
            lang={lang}
            allLanguageData={lang}
            productLanguages={productLanguages}
            currentLanguage={currentLanguage}
            onSetLanguage={onSetLanguage}
            templateAfter={templateAfter}
            isCustomUi={isCustomUi}
            customTemplateHtml={customTemplateHtml}
        />
        <ProductPagePreviewDialog
            isOpen={isPreviewDialogOpen}
            handleClose={() => setIsPreviewDialogOpen(false)}
            isEditTemplate={false}
        />
      </>
  )
}

